import find from 'lodash/find';
import React, { Fragment, useContext, useEffect } from 'react';
import { matchPath, RouteComponentProps, withRouter } from 'react-router-dom';
import Typo3Error from '../../components/behavior/Typo3Error';
import { Content } from '../../components/Content';
import { Column, Row } from '../../components/Grid';
import { MainContentContainer } from '../../components/Main';
import { Spinner } from '../../components/Spinner';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import {
  withBreakpoints,
  WithBreakpointsProps
} from '../../provider/BreakpointProvider/withBreakpoints';
import { PAGE_SEARCH } from '../../provider/Typo3Provider/config/t3PageMapping';
import {
  selectContent,
  selectMenu,
  selectMeta,
  selectSearchMenuEntry,
  selectStage
} from '../../provider/Typo3Provider/selectors';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import { UsercentricsContext } from "../../provider/UsercentricsProvider";
import IntroSearch from '../IntroSearch';
import AsyncLocationContainer from '../LandingPages/LocationContainer/AsyncLocationContainer';
import BookmarkOverlayTipContainer from './components/BookmarkOverlayTipContainer';
import BreadcrumbContainer from './components/BreadcrumbContainer';
import CarouselContainer from './components/CarouselContainer';
import CountryKeyvisual from './components/CountryKeyvisual';
import HelmetContainer from './components/HelmetContainer';
import NewsletterContainer from './components/NewsletterContainer';
import SocialMediaContainer from './components/SocialMediaContainer';
import StickySideElement from './components/StickySideElement';
import { getComponent } from './ComponentSelector';
import SchemaOrg from './SchemaOrg';

interface ContentContainerProps {}


const ContentContainer = ({
  location,
  breakpoints,
  typo3
}: ContentContainerProps &
  WithBreakpointsProps &
  WithTypo3Props &
  RouteComponentProps) => {
  const meta = selectMeta(typo3);
  const content = selectContent(typo3);
  const stage = selectStage(typo3);

  const usercentrics = useContext(UsercentricsContext);

  useEffect(() => {
      usercentrics.reload();
    },
    [content, usercentrics])

  if (!meta) {
    // Content is not ready yet
    return <Spinner />;
  }

  const { large, medium } = breakpoints;
  const doktype =
    meta.page && meta.page.doktype && meta.page.doktype === 52
      ? 'magazine'
      : 'default';
  const showBreadcrumb = meta.page.pid !== 0 && meta.page.uid !== PAGE_SEARCH;

  const [rootPage = null] = selectMenu(typo3, 'rootpage');
  const main = selectMenu(typo3, 'main');

  const isInMainNavi = !!main.find(item => {
    return (
      item.link === location.pathname ||
      !!find(item.children, child => {
        return child.link === location.pathname;
      })
    );
  });

  const showIntroSearch =
    (rootPage && rootPage.current) ||
    (isInMainNavi &&
      content.every(
        ({ CType }) => !['search', 'searchdetail'].includes(CType)
      ));

  const showLocationContainer =
    meta.page && meta.page.doktype && meta.page.doktype === 51;

  const searchEntry = selectSearchMenuEntry(typo3);
  const isPrintVersion =
    searchEntry && searchEntry.link
      ? !!matchPath(location.pathname, {
          path: searchEntry.link,
          exact: true,
          strict: true
        }) && !new URLSearchParams(location.search).has('print')
      : false;

  return (
    <Content>
      <HelmetContainer />

      <SchemaOrg page={meta.page} />

      {medium && <StickySideElement />}

      {large && !!stage.length && showIntroSearch && <IntroSearch />}

      {stage.length > 0 &&
        (showLocationContainer ? (
          <CountryKeyvisual />
        ) : (
          <CarouselContainer isRoot={!!rootPage && !!rootPage.current} />
        ))}

      {showBreadcrumb && <BreadcrumbContainer />}

      <MainContentContainer doktype={doktype}>
        <Row noGap>
          <Column>
            <Typo3Error />
            {content.map(getComponent)}
          </Column>
        </Row>
      </MainContentContainer>

      {showLocationContainer && <AsyncLocationContainer />}

      {!isPrintVersion && (
        <Fragment>
          <BookmarkOverlayTipContainer />
          <SocialMediaContainer />
          <NewsletterContainer />
        </Fragment>
      )}
    </Content>
  );
};

export default withErrorBoundary(
  withRouter(withTypo3(withBreakpoints(ContentContainer))),
  'ContentContainer'
);
